/**
 * Spiderchart de compétences (réutilisabilité — Étape 4).
 * Ajouté dans l'onglet « Ma progression ». Lit le graphe (Étape 3) :
 * maîtrise par phase (radar) + barres + tendance + time-to-skill + drill-down critères.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, TrendingUp, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Info } from "lucide-react";
import { competencyApi, drillSessionApi, type CompetencyPhase } from "../lib/api";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { DrillIcon } from "./DrillIcon";
import { launchDrill, lowest3Drillable } from "../lib/drill-launch";
import { useDrillVisible } from "../hooks/useDrillVisible";

const R = 105;
const CX = 150;
const CY = 145;

function pt(i: number, frac: number): [number, number] {
  const a = ((-90 + i * 72) * Math.PI) / 180;
  const r = frac * R;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}
function ringPoints(frac: number): string {
  return [0, 1, 2, 3, 4].map((i) => pt(i, frac).join(",")).join(" ");
}

export function CompetencyChart({ onNavigate, profile }: { onNavigate?: (page: import("../types").PageType) => void; profile?: CompetencyPhase[] } = {}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [phases, setPhases] = useState<CompetencyPhase[] | null>(profile ?? null);
  const [loading, setLoading] = useState(!profile);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  // « S'exercer » : critères éligibles (fiche active) → map clé→simulateur + set des clés.
  const [drillSimByKey, setDrillSimByKey] = useState<Map<string, string>>(new Map());
  // Seconde protection de l'écran (13/10/2026) : S'exercer fermé → aucune icône, même si le serveur en servait une.
  const drillOuvert = useDrillVisible();
  useEffect(() => {
    if (!drillOuvert || !onNavigate) { setDrillSimByKey(new Map()); return; }
    let alive = true;
    drillSessionApi.eligibleCriteria().then((rows) => {
      if (!alive) return;
      const m = new Map<string, string>();
      for (const r of rows) if (!m.has(r.criterionKey)) m.set(r.criterionKey, r.simulatorId);
      setDrillSimByKey(m);
    }).catch(() => {});
    return () => { alive = false; };
  }, [drillOuvert, onNavigate]);

  const drillableKeys = useMemo(() => {
    if (!onNavigate || !phases) return new Set<string>();
    const all = phases.flatMap((p) => p.criteria.map((c) => ({ key: c.key, score: c.score })));
    return lowest3Drillable(all, new Set(drillSimByKey.keys()));
  }, [phases, drillSimByKey, onNavigate]);
  const launchDrillFromProgress = (c: { key: string; labelFr?: string; labelEn?: string }) => {
    const simulatorId = drillSimByKey.get(c.key);
    if (!onNavigate || !simulatorId) return;
    launchDrill({ criterionKey: c.key, criterionLabel: pickScoringLabel({ labelFr: c.labelFr, labelEn: c.labelEn }, lang), simulatorId, origin: "progress" }, onNavigate);
  };

  useEffect(() => {
    // Profil fourni (ex. diagnostic scopé à l'évaluation) → pas de fetch.
    if (profile) { setPhases(profile); setLoading(false); return; }
    let alive = true;
    competencyApi
      .getMine()
      .then((d) => {
        if (alive) setPhases(d.phases);
      })
      .catch(() => {
        if (alive) setPhases(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading)
    return (
      <div className="rounded-2xl border bg-white p-6 shadow-sm flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#384872" }} />
      </div>
    );
  if (!phases) return null;

  const phaseName = (k: string) => t(`weeklyRhythm.phase.${k}`);
  const hasAny = phases.some((p) => p.score != null);
  const dataPoints = phases.map((p, i) => pt(i, (p.score ?? 0) / 100).join(",")).join(" ");

  const withData = phases.filter((p) => p.score != null);
  const weakest = withData.length ? withData.reduce((a, b) => (a.score! <= b.score! ? a : b)) : null;
  let ttsText = "";
  if (weakest) {
    if (weakest.timeToSkillWeeks === 0) ttsText = t("weeklyRhythm.competency.mastered");
    else if (weakest.timeToSkillWeeks && weakest.timeToSkillWeeks > 0)
      ttsText = t("weeklyRhythm.competency.tts", { count: weakest.timeToSkillWeeks, phase: phaseName(weakest.phaseKey) });
    else ttsText = t("weeklyRhythm.competency.keepTraining");
  }

  return (
    <div className="rounded-2xl border bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5" style={{ color: "#384872" }} />
        <h2 className="text-lg font-semibold flex-1" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          {t("weeklyRhythm.competency.title")}
        </h2>
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          aria-label={t("weeklyRhythm.competency.helpAria")}
          aria-expanded={showHelp}
          className="shrink-0 rounded-full p-1 hover:bg-slate-50"
        >
          <Info className="w-4 h-4" style={{ color: showHelp ? "#384872" : "#9AA0B0" }} />
        </button>
      </div>
      <p className="text-sm mb-4" style={{ color: "#9AA0B0" }}>
        {t("weeklyRhythm.competency.subtitle")}
      </p>

      {showHelp && (
        <div
          className="mb-4 rounded-xl border p-4 text-sm space-y-1.5"
          style={{ borderColor: "#E7E9F0", background: "#F7F8FB", color: "#5B6478" }}
        >
          <p className="font-medium" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
            {t("weeklyRhythm.competency.helpTitle")}
          </p>
          <p>{t("weeklyRhythm.competency.help1")}</p>
          <p>{t("weeklyRhythm.competency.help2")}</p>
          <p>{t("weeklyRhythm.competency.help3")}</p>
          <p>{t("weeklyRhythm.competency.help4")}</p>
        </div>
      )}

      {!hasAny ? (
        <p className="text-sm py-6 text-center" style={{ color: "#9AA0B0" }}>
          {t("weeklyRhythm.competency.noData")}
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 items-center">
          {/* Radar */}
          <div className="flex flex-col items-center">
            <svg viewBox="-60 -15 420 330" width="320" role="img" aria-label="Radar 5 phases">
              {[0.25, 0.5, 0.75, 1].map((f) => (
                <polygon key={f} points={ringPoints(f)} fill="none" stroke="#E7E9F0" strokeWidth="1" />
              ))}
              {[0, 1, 2, 3, 4].map((i) => {
                const [x, y] = pt(i, 1);
                return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="#E7E9F0" strokeWidth="1" />;
              })}
              <polygon points={dataPoints} fill="rgba(124,191,148,.28)" stroke="#7CBF94" strokeWidth="2" />
              {phases.map((p, i) => {
                const [x, y] = pt(i, (p.score ?? 0) / 100);
                return <circle key={i} cx={x} cy={y} r="3.5" fill={p.score == null ? "#CBD0DE" : "#7CBF94"} />;
              })}
              {phases.map((p, i) => {
                const [x, y] = pt(i, 1.15);
                const anchor = Math.abs(x - CX) < 12 ? "middle" : x > CX ? "start" : "end";
                return (
                  <text key={i} x={x} y={y} textAnchor={anchor} fontFamily="Sora,sans-serif" fontSize="12" fontWeight="500" fill="#384872">
                    {phaseName(p.phaseKey)}
                  </text>
                );
              })}
            </svg>
            {weakest && (
              <p className="text-sm mt-2 text-center" style={{ color: "#384872" }}>
                {ttsText}
              </p>
            )}
            <p className="text-xs mt-1" style={{ color: "#9AA0B0" }}>
              {t("weeklyRhythm.competency.source")}
            </p>
          </div>

          {/* Liste des phases + drill-down */}
          <div className="space-y-2">
            {phases.map((p) => {
              const has = p.score != null;
              const low = has && p.confidence < 0.5;
              const isOpen = expanded === p.phaseKey;
              return (
                <div key={p.phaseKey} className="border rounded-xl" style={{ borderColor: "#E7E9F0" }}>
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                    onClick={() => has && setExpanded(isOpen ? null : p.phaseKey)}
                  >
                    <span className="text-sm font-medium flex-1" style={{ color: "#384872", fontFamily: "Sora,sans-serif" }}>
                      {phaseName(p.phaseKey)}
                    </span>
                    {has ? (
                      <>
                        {p.trend != null && p.trend !== 0 && (
                          <span className="inline-flex items-center text-xs" style={{ color: p.trend > 0 ? "#1E7A3C" : "#C0423F" }}>
                            {p.trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                            {Math.abs(p.trend)}
                          </span>
                        )}
                        <span className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: "#EEF0F6" }}>
                          <span className="block h-full rounded-full" style={{ width: `${p.score}%`, background: low ? "#CBD0DE" : "#7CBF94" }} />
                        </span>
                        <span className="text-sm tabular-nums w-8 text-right" style={{ color: low ? "#9AA0B0" : "#384872" }}>
                          {p.score}
                        </span>
                        {p.criteria.length > 0 &&
                          (isOpen ? (
                            <ChevronUp className="w-4 h-4" style={{ color: "#9AA0B0" }} />
                          ) : (
                            <ChevronDown className="w-4 h-4" style={{ color: "#9AA0B0" }} />
                          ))}
                      </>
                    ) : (
                      <span className="text-xs" style={{ color: "#9AA0B0" }}>
                        {t("weeklyRhythm.competency.noData")}
                      </span>
                    )}
                  </button>
                  {isOpen && p.criteria.length > 0 && (
                    <div className="px-3 pb-3 space-y-1.5">
                      {low && (
                        <p className="text-xs italic mb-1" style={{ color: "#9AA0B0" }}>
                          {t("weeklyRhythm.competency.notEnough")}
                        </p>
                      )}
                      {p.criteria.map((c) => (
                        <div key={c.key} className="flex items-center gap-2 text-sm">
                          <span className="flex-1 flex items-center gap-1.5" style={{ color: "#5B6478" }}>
                            {pickScoringLabel({ labelFr: c.labelFr, labelEn: c.labelEn }, lang)}
                            {drillableKeys.has(c.key) && (
                              <DrillIcon onClick={() => launchDrillFromProgress(c)} tooltip={t("drill.iconTooltip")} size={13} />
                            )}
                          </span>
                          <span className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: "#EEF0F6" }}>
                            <span className="block h-full rounded-full" style={{ width: `${c.score}%`, background: "#988EF2" }} />
                          </span>
                          <span className="tabular-nums w-7 text-right text-xs" style={{ color: "#384872" }}>
                            {c.score}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
